<template>
  <div class="scan-page">
    <header class="scan-header">
      <img src="../assets/logo.png" alt="El Shaday" class="scan-logo" />
      <h1 class="scan-title">Check-in</h1>
      <p class="scan-subtitle" v-if="mode === 'choose' && !result && !pendingCheckin && !processing">
        Escaneie o QR Code do acampante
      </p>
    </header>

    <!-- Loading -->
    <div class="scan-loading" v-if="processing">
      <div class="spinner"></div>
      <span>Processando...</span>
    </div>

    <!-- Modal de confirmação de transporte (1-tap, reuso da Fase 7) -->
    <TransporteModal
      :visible="!!pendingCheckin"
      :participant="pendingCheckin"
      :loading="confirming"
      @select="handleSelect"
      @cancel="cancelCheckin"
    />

    <!-- Resultado final (success / warning / error) -->
    <ScanResult
      v-if="result"
      :mode="result.mode"
      :participant="result.participant"
      :errorMessage="result.errorMessage"
      @restart="resetScan"
    />

    <!-- Câmera ao vivo -->
    <div class="scanner-wrapper" v-else-if="mode === 'camera' && !pendingCheckin">
      <div class="scanner-controls">
        <button class="camera-toggle" @click="switchCamera" v-if="cameras.length > 1">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
          <span>Trocar câmera</span>
        </button>
        <button class="camera-toggle" @click="returnToChoose">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M19 11H7.83l4.88-4.88c.39-.39.39-1.03 0-1.42a.9959.9959 0 0 0-1.41 0l-6.59 6.59a.9959.9959 0 0 0 0 1.41l6.59 6.59a.9959.9959 0 0 0 1.41 0c.39-.39.39-1.02 0-1.41L7.83 13H19c.55 0 1-.45 1-1s-.45-1-1-1z"/></svg>
          <span>Voltar</span>
        </button>
      </div>
      <div class="scanner-container">
        <div id="qr-reader" class="qr-reader"></div>
      </div>
    </div>

    <!-- Escolha de modo (default) -->
    <div class="options-card card" v-else-if="mode === 'choose' && !pendingCheckin">
      <button class="option-btn" @click="tryCamera" v-if="canUseCamera">
        <div class="option-icon option-camera">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
        </div>
        <div class="option-text">
          <span class="option-label">Abrir câmera</span>
          <span class="option-desc">Escanear QR em tempo real</span>
        </div>
      </button>

      <label class="option-btn">
        <div class="option-icon option-photo">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4l2 3l3-4l4 5H5z"/></svg>
        </div>
        <div class="option-text">
          <span class="option-label">Tirar foto do QR</span>
          <span class="option-desc">Usa a câmera nativa do celular</span>
        </div>
        <input type="file" accept="image/*,.heic,.heif" capture="environment" class="file-input" @change="onFileSelected" />
      </label>

      <label class="option-btn">
        <div class="option-icon option-gallery">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/></svg>
        </div>
        <div class="option-text">
          <span class="option-label">Escolher da galeria</span>
          <span class="option-desc">Selecionar imagem salva</span>
        </div>
        <input type="file" accept="image/*,.heic,.heif" class="file-input" @change="onFileSelected" />
      </label>

      <p class="camera-note" v-if="!canUseCamera">
        Câmera em tempo real requer HTTPS. Use as opções acima.
      </p>
    </div>

    <!-- Container oculto pro scanFile do html5-qrcode -->
    <div id="qr-reader-file-temp" style="display:none;"></div>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import apiClient from '../lib/api'
import TransporteModal from '../components/TransporteModal.vue'
import ScanResult from '../components/ScanResult.vue'

export default {
  name: 'ScanPage',
  components: { TransporteModal, ScanResult },
  data() {
    return {
      mode: 'choose',           // 'choose' | 'camera'
      scanner: null,
      scanning: false,
      processing: false,
      pendingCheckin: null,     // participant retornado pelo dryRun
      confirming: null,         // 'PROPRIO' | 'ONIBUS' enquanto o scan real está em curso
      result: null,             // { mode, participant?, errorMessage? }
      cameras: [],
      currentCameraIndex: 0,
      usingFront: false
    }
  },
  computed: {
    canUseCamera() {
      return typeof window !== 'undefined' && window.isSecureContext
    }
  },
  beforeUnmount() {
    this.stopScanner()
  },
  methods: {
    async tryCamera() {
      try {
        this.cameras = await Html5Qrcode.getCameras()
        if (!this.cameras || this.cameras.length === 0) {
          this.result = { mode: 'error', errorMessage: 'Nenhuma câmera encontrada. Use foto ou galeria.' }
          return
        }
        this.mode = 'camera'
        await this.$nextTick()
        this.startScanner()
      } catch {
        this.result = { mode: 'error', errorMessage: 'Permissão de câmera negada.' }
      }
    },

    async startScanner() {
      try {
        this.scanner = new Html5Qrcode('qr-reader')
        const cameraConfig = this.cameras.length > 0
          ? { deviceId: { exact: this.cameras[this.currentCameraIndex].id } }
          : { facingMode: this.usingFront ? 'user' : 'environment' }
        await this.scanner.start(
          cameraConfig,
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
          text => this.onScanSuccess(text),
          () => {}
        )
        this.scanning = true
      } catch {
        this.mode = 'choose'
        this.result = { mode: 'error', errorMessage: 'Erro ao iniciar câmera.' }
      }
    },

    async stopScanner() {
      if (this.scanner && this.scanning) {
        try { await this.scanner.stop() } catch { /* já parado */ }
        this.scanning = false
      }
    },

    async switchCamera() {
      await this.stopScanner()
      if (this.cameras.length > 1) {
        this.currentCameraIndex = (this.currentCameraIndex + 1) % this.cameras.length
      }
      this.usingFront = !this.usingFront
      await this.$nextTick()
      this.startScanner()
    },

    async returnToChoose() {
      await this.stopScanner()
      this.mode = 'choose'
    },

    normalizeImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const maxSize = 1024
          let width = img.width
          let height = img.height
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob(
            blob => blob ? resolve(new File([blob], 'qr.png', { type: 'image/png' })) : reject(new Error('Canvas conversion failed')),
            'image/png'
          )
        }
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = URL.createObjectURL(file)
      })
    },

    async onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.processing = true
      let tempScanner
      try {
        const normalized = await this.normalizeImage(file)
        tempScanner = new Html5Qrcode('qr-reader-file-temp')
        const text = await tempScanner.scanFile(normalized, true)
        await this.doScan(text)
      } catch {
        this.result = { mode: 'error', errorMessage: 'Não foi possível ler o QR. Tente uma foto mais nítida.' }
      } finally {
        this.processing = false
        if (tempScanner) {
          try { tempScanner.clear() } catch { /* ignore */ }
        }
        event.target.value = ''
      }
    },

    async onScanSuccess(text) {
      if (this.processing || this.pendingCheckin || this.result) return
      this.processing = true
      await this.stopScanner()
      await this.doScan(text)
      this.processing = false
    },

    async doScan(participantId) {
      try {
        const res = await apiClient.post(`/scan/${participantId}`, { dryRun: true })
        if (res.data.alreadyCheckedIn) {
          this.result = { mode: 'warning', participant: res.data.participant }
          return
        }
        this.pendingCheckin = res.data.participant
      } catch (err) {
        const message = err.response?.data?.message || 'Não foi possível ler o QR ou o acampante não existe.'
        this.result = { mode: 'error', errorMessage: message }
      }
    },

    async handleSelect(transporte) {
      if (!this.pendingCheckin) return
      const target = this.pendingCheckin
      this.confirming = transporte
      try {
        const res = await apiClient.post(`/scan/${target.id}`, { transporte })
        if (res.data.alreadyCheckedIn) {
          this.result = { mode: 'warning', participant: res.data.participant }
        } else {
          this.result = { mode: 'success', participant: res.data.participant }
        }
        this.pendingCheckin = null
      } catch (err) {
        const message = err.response?.data?.message || 'Erro ao confirmar check-in.'
        this.result = { mode: 'error', errorMessage: message }
        this.pendingCheckin = null
      } finally {
        this.confirming = null
      }
    },

    cancelCheckin() {
      if (this.confirming) return
      this.pendingCheckin = null
      this.resetScan()
    },

    async resetScan() {
      this.result = null
      this.pendingCheckin = null
      this.confirming = null
      if (this.mode === 'camera') {
        await this.$nextTick()
        this.startScanner()
      }
    }
  }
}
</script>

<style scoped>
.scan-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-lg) var(--spacing-base);
  gap: var(--spacing-lg);
  background: var(--color-bg-body);
}
.scan-header { text-align: center; }
.scan-logo {
  width: 64px;
  height: 64px;
  border-radius: var(--radius-circle);
  object-fit: cover;
  margin-bottom: var(--spacing-sm);
}
.scan-title {
  font-size: var(--text-xl);
  font-weight: 800;
  color: var(--color-text-primary);
  margin: 0;
}
.scan-subtitle {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  margin-top: var(--spacing-xs);
}

.scan-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--color-text-muted);
}
.spinner {
  width: 36px; height: 36px;
  border: 3px solid var(--color-bg-separator);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.options-card {
  width: 100%;
  max-width: 420px;
  padding: var(--spacing-base);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
.option-btn {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-base);
  padding: var(--spacing-base);
  border: 2px solid var(--color-bg-separator);
  border-radius: var(--radius-lg);
  background: var(--color-bg-surface);
  cursor: pointer;
  transition: var(--transition-fast);
  text-align: left;
  text-decoration: none;
}
.option-btn:hover {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-button);
  transform: translateY(-1px);
}
.option-icon {
  width: 56px; height: 56px;
  border-radius: var(--radius-lg);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.option-camera { background: rgba(51, 102, 204, 0.12); color: var(--color-accent); }
.option-photo { background: rgba(46, 204, 113, 0.12); color: var(--color-success); }
.option-gallery { background: rgba(155, 89, 182, 0.12); color: #9b59b6; }
.option-text { display: flex; flex-direction: column; }
.option-label {
  font-size: var(--text-md);
  font-weight: 700;
  color: var(--color-text-primary);
}
.option-desc {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin-top: 2px;
}
.file-input {
  position: absolute; inset: 0;
  opacity: 0; cursor: pointer;
}
.camera-note {
  text-align: center;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--color-bg-separator);
}

.scanner-wrapper {
  width: 100%;
  max-width: 420px;
}
.scanner-controls {
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}
.camera-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 8px 14px;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-fast);
}
.camera-toggle:hover { border-color: var(--color-accent); color: var(--color-accent); }
.scanner-container {
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-card);
}
.qr-reader { width: 100%; }
</style>
